import React, {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {selectDataTableSelection} from 'src/components/data-table-selection/services/data-table-selection-slice';

import Button from '@pb/kit-component-button';
import {DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {pluralize} from '@pb/kit-formatters';


import {TOOL_TITLE} from './constants';

import type {ProcessBulkAction} from 'src/components/bulk-tool/typedef';
import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {UpdateUserEntitlementsRelation} from 'src/openapi/admin/models/UpdateUserEntitlementsRelation';
import type {AppState} from 'src/store/typedef';

type Props = {
    userId: number,
    selectionKey: string,
    onCloseModal: (submit: boolean) => void,
    processAction: ProcessBulkAction<UpdateUserEntitlementsRelation>,
};

export const UnassignProductUserEntitlementsModal = (
    {userId, selectionKey, onCloseModal, processAction}: Props,
) => {
    const selection = useSelector(
        (state: AppState) => selectDataTableSelection(state, selectionKey),
    );

    const grantIds = useMemo(
        () => [...new Set(selection.ids.map(id => JSON.parse(id.toString()).grantId).filter(Boolean))],
        [selection.ids],
    );

    const roleIds = useMemo(
        () => selection.ids.map(id => JSON.parse(id.toString()).roleId).filter(Boolean),
        [selection.ids],
    );

    const unassign = useCallback(async (options?: ActionOptions) => {
        return await processAction({
            userId,
            roleIds: roleIds.length ? roleIds : undefined,
            grantIds: grantIds.length ? grantIds : undefined,
        }, options);
    }, [roleIds, grantIds, userId, processAction]);

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>
                {TOOL_TITLE}
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                Are you sure you want to unassign selected {[
                    roleIds.length ? pluralize('role[s]', roleIds.length) : '',
                    grantIds.length ? pluralize('grant[s]', grantIds.length) : '',
                ].filter(Boolean).join(' and ')} from the user?
            </DialogMain>
            <BulkActionFooter action="Unassign" onConfirm={unassign}>
                <Group>

                    <Button variant="Type.SECONDARY" onClick={() => onCloseModal(false)}>
                        Cancel
                    </Button>
                </Group>
            </BulkActionFooter>
        </>
    );
};
